import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

const CollapsibleFilter = ({
  headerText,
  children,
  icon,
  className = "",
  headerClassName = "",
  contentClassName = "",
  defaultOpen = false,
  isOpen,
  onToggle,
  disabled = false,
  transitionDuration = 300, // ms, you can override
  headerButtonRef
}) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const open = typeof isOpen === "boolean" ? isOpen : internalOpen;

  const contentRef = useRef(null);
  const [measuredHeight, setMeasuredHeight] = useState(0);
  useEffect(() => {
    const el = contentRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      if (el) setMeasuredHeight(el.scrollHeight);
      return;
    }
    const observer = new ResizeObserver(() => {
      setMeasuredHeight(el.scrollHeight);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleToggle = () => {
    if (disabled) return;

    const next = !open;

    if (typeof isOpen !== "boolean") {
      setInternalOpen(next);
    }

    onToggle?.(next);
  };

  const renderIcon = () => {
    if (icon) return icon;
    return <ChevronDown size={16} strokeWidth={2} />;
  };

  // inline transition styles
  const bodyStyle = {
    maxHeight: open ? `${measuredHeight}px` : "0px",
    overflow: "hidden",
    transition: `max-height ${transitionDuration}ms ease, opacity ${
      transitionDuration / 1.5
    }ms ease, transform ${transitionDuration}ms ease`,
    opacity: open ? 1 : 0,
    transform: open ? "translateY(0)" : "translateY(-6px)",
  };

  return (
    <div
      className={[
        "w-full rounded-md border border-[#E5E7EB] bg-white shadow-sm",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {/* Header */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        aria-expanded={open}
        ref={headerButtonRef}
        className={[
          "w-full flex items-center justify-between px-3 py-2",
          "text-sm text-[#374151]",
          disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
          headerClassName,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <span className="truncate text-left">{headerText}</span>

        <span
          className={[
            "flex items-center justify-center transition-transform",
            `duration-[${transitionDuration}ms]`,
            open ? "rotate-180" : "rotate-0",
          ].join(" ")}
          aria-hidden
        >
          {renderIcon()}
        </span>
      </button>
      <div
        style={bodyStyle}
        className={[contentClassName, "px-4"].filter(Boolean).join(" ")}
        aria-hidden={!open}
      >
        <div ref={contentRef} className="py-3 text-sm">
          {children}
        </div>
      </div>
    </div>
  );
};

export default CollapsibleFilter;
